import { Button } from "@chakra-ui/react";
import { PostFeesModal } from "features/fees/post/PostFeesModal";
import { useFeeStatsRetrieveSuspense } from "gen/endpoints/fee-stats/fee-stats";
import { FeeStatData } from "gen/model/feeStatData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageTabs, PageTabsStatus } from "shared/components/layout/PageTabs";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export interface FeeTabProps {
  statsData: FeeStatData | undefined;
}

export const FeesPage: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["fees", "common"]);

  const { data: statsData } = useFeeStatsRetrieveSuspense(fundFamilyId);
  const selectTimeRange = useConfirmationDialog(PostFeesModal, {
    fundFamilyId,
    lastChargedPeriod: statsData.last_charged_period_rich_text,
  });

  const postFees = useCallback(async () => {
    const data = await selectTimeRange();
    if (!data) {
      return;
    }
    const { feeChargeEventId } = data;
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .fees({})
        .post({ feeChargeEventId }).$,
    );
  }, [fundFamilyId, navigate, selectTimeRange]);

  const tabProps: FeeTabProps = { statsData };

  const hiddenTabs: PageTabsStatus = {
    feePostingTab: false,
  };

  return (
    <PageContent>
      <PageHeader
        title={t("fees:fees")}
        actions={
          <Restricted permission={PermissionTypeEnum.write}>
            <Button onClick={postFees} colorScheme="secondary" size="sm">
              {t("fees:postFees.title")}
            </Button>
          </Restricted>
        }
      />
      <PageBody>
        <PageTabs context={tabProps} hiddenTabs={hiddenTabs} />
      </PageBody>
    </PageContent>
  );
};
